import React, { useCallback, useContext, useRef } from 'react'

import { Oval } from 'react-loader-spinner'

import { Checkbox, Paper, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { ScanRowsContext } from '../../pages/ScanPage'
import {
  DataGridTableContainer,
  DataGridTableHeaderCell,
  DataGridTableLoaderDiv,
  DataGridTableRowHead,
  DataGridTableStyled,
} from '../../styles/components/DataGridTableStyle'

const scanColumns = [
  { field: 'fazit', headerName: 'FAZIT' },
  { field: 'scanned_at', headerName: 'Scanned At' },
  { field: 'computer', headerName: 'Computer' },
  { field: 'filter_final_ECU_number', headerName: 'Filter Final ECU Number' },
  { field: 'filter_final_sw', headerName: 'Filter Final SW' },
  { field: 'filter_date_from', headerName: 'Filter Date From' },
  { field: 'filter_date_to', headerName: 'Filter Date To' },
  { field: 'username', headerName: 'Username' },
  { field: 'report', headerName: 'Report' },
  { field: 'report_diag_address', headerName: 'Address' },
  { field: 'report_date', headerName: 'Date' },
  { field: 'report_sequence_id', headerName: 'Sequence ID' },
  { field: 'report_description', headerName: 'Description' },
]

const getDisplayValue = (field, value) => {
  if (value === null || value === undefined) {
    return ''
  }

  if (field === 'report') {
    return value ? 'YES' : 'NO'
  }

  return String(value)
}

const checkboxCellSx = {
  width: '2.5rem',
  minWidth: '2.5rem',
  maxWidth: '2.5rem',
  padding: '0 0.25rem',
  textAlign: 'center',
}

const getRowIdentifier = (row) => {
  if (row.id === undefined || row.id === null) {
    return null
  }

  return row.id
}

const ScanTable = () => {
  const theme = useTheme()
  const { rows, selectedRows, setSelectedRows, isLoading, isResultEmpty, hasNextPage, setCurrentPage, setIsLoading } =
    useContext(ScanRowsContext)

  const observer = useRef()

  const lastRowElementRef = useCallback(
    (node) => {
      if (isLoading) {
        return
      }

      if (observer.current) {
        observer.current.disconnect()
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          setCurrentPage((prev) => prev + 1)
          setIsLoading(true)
        }
      })

      if (node) {
        observer.current.observe(node)
      }
    },
    [isLoading, hasNextPage, setCurrentPage, setIsLoading]
  )

  const handleRowSelection = (row) => {
    const rowId = getRowIdentifier(row)
    if (rowId === null) {
      return
    }

    setSelectedRows((prevState) => {
      const nextState = new Set(prevState)
      if (nextState.has(rowId)) {
        nextState.delete(rowId)
      } else {
        nextState.add(rowId)
      }
      return nextState
    })
  }

  return (
    <DataGridTableContainer component={Paper} elevation={0}>
      <DataGridTableStyled>
        <TableHead>
          <DataGridTableRowHead>
            <TableCell sx={checkboxCellSx}></TableCell>
            {scanColumns.map((column) => (
              <DataGridTableHeaderCell key={column.field} width="12rem">
                {column.headerName}
              </DataGridTableHeaderCell>
            ))}
          </DataGridTableRowHead>
        </TableHead>
        <TableBody>
          {!isResultEmpty &&
            rows.map((row, index) => (
              <TableRow key={row.id ?? `${row.fazit}-${row.scanned_at}-${index}`} ref={index === rows.length - 1 ? lastRowElementRef : null}>
                <TableCell sx={checkboxCellSx}>
                  <Checkbox
                    checked={selectedRows.has(getRowIdentifier(row))}
                    onChange={() => handleRowSelection(row)}
                    color="primary"
                  />
                </TableCell>
                {scanColumns.map((column) => (
                  <TableCell key={`${column.field}-${index}`} align="center">
                    {getDisplayValue(column.field, row[column.field])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </DataGridTableStyled>

      {(isLoading || isResultEmpty) && (
        <DataGridTableLoaderDiv>
          {isLoading ? (
            <Oval type="TailSpin" color={theme.palette.primary.light} />
          ) : (
            <Typography variant="h5" component="h3" sx={{ fontWeight: 600, textAlign: 'center' }}>
              No data found
            </Typography>
          )}
        </DataGridTableLoaderDiv>
      )}
    </DataGridTableContainer>
  )
}

export default ScanTable
